import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import { api, formatErr } from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Checkbox } from "./ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { EmptyState } from "./EmptyState";
import {
  Megaphone, Plus, Trash2, Pencil, Eye, MousePointerClick, BarChart2,
  TrendingUp, Image, Calendar, MapPin, Tag, Upload, X
} from "lucide-react";

const ALL_DISTRICTS = [
  "Aveiro","Beja","Braga","Bragança","Castelo Branco","Coimbra","Faro",
  "Guarda","Leiria","Lisboa","Portalegre","Porto","Santarém","Setúbal",
  "Viana do Castelo","Vila Real","Viseu","Évora","Madeira","Açores"
];
const ALL_CATEGORIES = [
  "Restauração","Logística","Retalho","Hotelaria","Limpeza",
  "Apoio ao Cliente","Construção","Saúde","Outros"
];
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_FORM = {
  name: "", placement: "", target_url: "", alt: "",
  priority: 0, active: true,
  start_at: "", end_at: "",
  districts: [], categories: [],
};

function MultiCheckbox({ label, icon: Icon, options, selected, onChange }) {
  const toggle = (v) => onChange(selected.includes(v) ? selected.filter(x => x !== v) : [...selected, v]);
  return (
    <div>
      <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-2 flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5" /> {label}
        {selected.length > 0 && (
          <span className="ml-auto text-xs font-normal text-amber-600 cursor-pointer hover:underline"
            onClick={() => onChange([])}>limpar</span>
        )}
      </p>
      <div className="flex flex-wrap gap-2 max-h-28 overflow-y-auto pr-1">
        {options.map(o => (
          <label key={o} className="flex items-center gap-1.5 text-sm cursor-pointer select-none">
            <Checkbox checked={selected.includes(o)} onCheckedChange={() => toggle(o)} />
            {o}
          </label>
        ))}
      </div>
      {selected.length === 0 && (
        <p className="text-[11px] text-slate-400 mt-1">Vazio = sem restrição (aparece em todos)</p>
      )}
    </div>
  );
}

function AdForm({ placements, initial, onSubmit, loading, showFileField = true }) {
  const [form, setForm] = useState({ ...EMPTY_FORM, ...initial });
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const fileRef = useRef();

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const submit = (e) => {
    e.preventDefault();
    if (showFileField && !file) { toast.error("Escolhe uma imagem"); return; }
    if (!form.placement) { toast.error("Escolhe a posição do banner"); return; }
    if (!form.target_url.startsWith("http")) { toast.error("O link de destino deve começar com http:// ou https://"); return; }
    onSubmit(form, file);
  };

  return (
    <form onSubmit={submit} className="space-y-5 overflow-y-auto max-h-[70vh] pr-1">
      {showFileField && (
        <div>
          <Label className="mb-1.5 block">Imagem do banner *</Label>
          <div
            className="border-2 border-dashed border-slate-200 rounded-xl p-4 text-center cursor-pointer hover:border-amber-400 transition-colors"
            onClick={() => fileRef.current?.click()}>
            {preview ? (
              <div className="relative">
                <img src={preview} alt="preview" className="max-h-24 mx-auto rounded-lg object-contain" />
                <button type="button" className="absolute top-1 right-1 rounded-full bg-white shadow p-0.5"
                  onClick={(e) => { e.stopPropagation(); setFile(null); setPreview(null); }}>
                  <X className="w-3 h-3 text-slate-600" />
                </button>
              </div>
            ) : (
              <div className="py-4">
                <Upload className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">Clica para escolher imagem (PNG/JPG/WEBP/GIF, máx. 3 MB)</p>
              </div>
            )}
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif"
              className="hidden" onChange={handleFile} />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="mb-1.5 block">Nome interno *</Label>
          <Input required value={form.name} onChange={e => set("name", e.target.value)}
            placeholder="Ex.: Lidl Lisboa Março" />
        </div>
        <div>
          <Label className="mb-1.5 block">Posição *</Label>
          <Select value={form.placement} onValueChange={v => set("placement", v)}>
            <SelectTrigger className="bg-white"><SelectValue placeholder="Escolhe a posição" /></SelectTrigger>
            <SelectContent>
              {Object.entries(placements).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label className="mb-1.5 block">Link de destino *</Label>
        <Input required value={form.target_url} onChange={e => set("target_url", e.target.value)}
          placeholder="https://www.anunciante.pt/campanha" type="url" />
      </div>

      <div>
        <Label className="mb-1.5 block">Texto alternativo (acessibilidade)</Label>
        <Input value={form.alt} onChange={e => set("alt", e.target.value)}
          placeholder="Descrição breve da imagem" />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 items-center">
        <div>
          <Label className="mb-1.5 block">Prioridade (0-10)</Label>
          <Input type="number" min="0" max="10" value={form.priority}
            onChange={e => set("priority", parseInt(e.target.value) || 0)} />
        </div>
        <div>
          <Label className="mb-1.5 block">Início (opcional)</Label>
          <Input type="date" value={form.start_at} onChange={e => set("start_at", e.target.value)} />
        </div>
        <div>
          <Label className="mb-1.5 block">Fim (opcional)</Label>
          <Input type="date" value={form.end_at} onChange={e => set("end_at", e.target.value)} />
        </div>
      </div>

      <MultiCheckbox label="Distritos" icon={MapPin} options={ALL_DISTRICTS}
        selected={form.districts} onChange={v => set("districts", v)} />
      <MultiCheckbox label="Categorias" icon={Tag} options={ALL_CATEGORIES}
        selected={form.categories} onChange={v => set("categories", v)} />

      <div className="flex items-center gap-3 pt-1">
        <Switch checked={form.active} onCheckedChange={v => set("active", v)} id="ad-active" />
        <Label htmlFor="ad-active" className="cursor-pointer">Ativo (visível imediatamente)</Label>
      </div>

      <DialogFooter className="pt-2 sticky bottom-0 bg-white pb-1">
        <Button type="submit" className="rounded-full bg-slate-900 hover:bg-slate-800 w-full sm:w-auto"
          disabled={loading}>
          {loading ? "A guardar..." : showFileField ? "Criar banner" : "Guardar alterações"}
        </Button>
      </DialogFooter>
    </form>
  );
}

function AdRow({ ad, placements, onRefresh }) {
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [loading, setLoading] = useState(false);

  const toggle = async () => {
    try {
      await api.patch(`/admin/ads/${ad.id}`, { active: !ad.active });
      onRefresh();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const del = async () => {
    setLoading(true);
    try {
      await api.delete(`/admin/ads/${ad.id}`);
      toast.success("Banner eliminado");
      setDeleting(false);
      onRefresh();
    } catch (e) { toast.error(formatErr(e)); setLoading(false); }
  };

  const update = async (form) => {
    setLoading(true);
    try {
      const payload = {
        name: form.name, placement: form.placement, target_url: form.target_url,
        alt: form.alt || undefined, priority: form.priority, active: form.active,
        start_at: form.start_at || undefined, end_at: form.end_at || undefined,
        districts: form.districts, categories: form.categories,
      };
      await api.patch(`/admin/ads/${ad.id}`, payload);
      toast.success("Banner atualizado");
      setEditing(false);
      onRefresh();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };

  const ctrColor = ad.ctr >= 2 ? "text-emerald-600" : ad.ctr >= 0.5 ? "text-amber-600" : "text-slate-500";

  return (
    <>
      <div data-testid={`admin-ad-${ad.id}`}
        className="bg-white rounded-xl border border-black/5 p-4 flex flex-wrap gap-4 items-start">
        {/* Image preview */}
        <a href={ad.target_url} target="_blank" rel="noreferrer"
          className="shrink-0 w-24 h-14 rounded-lg overflow-hidden border border-black/5 bg-slate-50 flex items-center justify-center">
          <img src={`${BASE_URL}${ad.image_url}`} alt={ad.alt}
            className="w-full h-full object-cover"
            onError={e => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }} />
          <div className="hidden w-full h-full items-center justify-center">
            <Image className="w-5 h-5 text-slate-300" />
          </div>
        </a>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <p className="font-heading font-bold tracking-tighter text-slate-900 truncate">{ad.name}</p>
            <Badge className={ad.active ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-slate-100 text-slate-500 hover:bg-slate-100"}>
              {ad.active ? "Ativo" : "Inativo"}
            </Badge>
          </div>
          <p className="text-xs text-slate-500 mb-2">
            {placements[ad.placement] || ad.placement}
            {ad.districts?.length > 0 && ` · ${ad.districts.join(", ")}`}
            {ad.categories?.length > 0 && ` · ${ad.categories.join(", ")}`}
            {ad.start_at && ` · de ${new Date(ad.start_at).toLocaleDateString("pt-PT")}`}
            {ad.end_at && ` até ${new Date(ad.end_at).toLocaleDateString("pt-PT")}`}
          </p>

          {/* Metrics */}
          <div className="flex flex-wrap gap-4 text-sm">
            <span className="flex items-center gap-1 text-slate-600">
              <Eye className="w-3.5 h-3.5 text-slate-400" /> {ad.impressions.toLocaleString("pt-PT")} impressões
            </span>
            <span className="flex items-center gap-1 text-slate-600">
              <MousePointerClick className="w-3.5 h-3.5 text-slate-400" /> {ad.clicks} cliques
            </span>
            <span className={`flex items-center gap-1 font-semibold ${ctrColor}`}>
              <TrendingUp className="w-3.5 h-3.5" /> CTR {ad.ctr}%
            </span>
            <span className="text-slate-400 text-xs">Prioridade: {ad.priority}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 shrink-0">
          <Switch checked={ad.active} onCheckedChange={toggle}
            aria-label="Ativar/desativar banner" />
          <Button size="icon" variant="ghost" className="rounded-full h-8 w-8"
            onClick={() => setEditing(true)}>
            <Pencil className="w-3.5 h-3.5" />
          </Button>
          <Button size="icon" variant="ghost" className="rounded-full h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
            onClick={() => setDeleting(true)}>
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {/* Edit dialog */}
      <Dialog open={editing} onOpenChange={setEditing}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading font-black tracking-tighter">Editar banner</DialogTitle>
          </DialogHeader>
          <AdForm placements={placements} showFileField={false}
            initial={{
              name: ad.name, placement: ad.placement, target_url: ad.target_url,
              alt: ad.alt || "", priority: ad.priority, active: ad.active,
              start_at: ad.start_at ? ad.start_at.slice(0, 10) : "",
              end_at: ad.end_at ? ad.end_at.slice(0, 10) : "",
              districts: ad.districts || [], categories: ad.categories || [],
            }}
            onSubmit={(form) => update(form)} loading={loading} />
        </DialogContent>
      </Dialog>

      {/* Delete confirm dialog */}
      <Dialog open={deleting} onOpenChange={setDeleting}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading font-black tracking-tighter">Eliminar banner</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">Tens a certeza que queres eliminar <strong>{ad.name}</strong>? Esta ação não pode ser revertida.</p>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => setDeleting(false)}>Cancelar</Button>
            <Button variant="destructive" className="rounded-full" onClick={del} disabled={loading}>
              {loading ? "A eliminar..." : "Eliminar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function AdminAdsTab() {
  const [ads, setAds] = useState([]);
  const [placements, setPlacements] = useState({});
  const [totals, setTotals] = useState({ impressions: 0, clicks: 0 });
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/ads")
      .then(r => { setAds(r.data.ads); setPlacements(r.data.placements); setTotals(r.data.totals); })
      .catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const createAd = async (form, file) => {
    setCreating(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("name", form.name);
      fd.append("placement", form.placement);
      fd.append("target_url", form.target_url);
      fd.append("alt", form.alt || "");
      fd.append("priority", String(form.priority));
      fd.append("active", String(form.active));
      fd.append("start_at", form.start_at || "");
      fd.append("end_at", form.end_at || "");
      fd.append("districts", form.districts.join(","));
      fd.append("categories", form.categories.join(","));
      await api.post("/admin/ads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Banner criado com sucesso!");
      setShowCreate(false);
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setCreating(false); }
  };

  const overallCtr = totals.impressions > 0
    ? (totals.clicks / totals.impressions * 100).toFixed(1)
    : "0.0";

  return (
    <div className="space-y-6">
      {/* Summary strip */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-black/5 p-4 text-center">
          <Eye className="w-5 h-5 text-slate-400 mx-auto mb-1" />
          <p className="font-heading font-black text-2xl text-slate-900">{totals.impressions.toLocaleString("pt-PT")}</p>
          <p className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mt-0.5">Impressões</p>
        </div>
        <div className="bg-white rounded-xl border border-black/5 p-4 text-center">
          <MousePointerClick className="w-5 h-5 text-slate-400 mx-auto mb-1" />
          <p className="font-heading font-black text-2xl text-slate-900">{totals.clicks}</p>
          <p className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mt-0.5">Cliques</p>
        </div>
        <div className="bg-white rounded-xl border border-black/5 p-4 text-center">
          <BarChart2 className="w-5 h-5 text-amber-500 mx-auto mb-1" />
          <p className="font-heading font-black text-2xl text-slate-900">{overallCtr}%</p>
          <p className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mt-0.5">CTR global</p>
        </div>
      </div>

      {/* Header + create button */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{ads.length} banner{ads.length !== 1 ? "s" : ""} configurado{ads.length !== 1 ? "s" : ""}</p>
        <Button data-testid="create-ad-btn" className="rounded-full bg-slate-900 hover:bg-slate-800"
          onClick={() => setShowCreate(true)}>
          <Plus className="w-4 h-4 mr-1.5" /> Novo banner
        </Button>
      </div>

      {/* Ads list */}
      {ads.length === 0 ? (
        <EmptyState icon={Megaphone} title="Sem banners configurados"
          description="Cria o primeiro banner publicitário para monetizar os espaços da plataforma." />
      ) : (
        <div className="space-y-3">
          {ads.map(ad => (
            <AdRow key={ad.id} ad={ad} placements={placements} onRefresh={load} />
          ))}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading font-black tracking-tighter">Novo banner</DialogTitle>
          </DialogHeader>
          <AdForm placements={placements} initial={EMPTY_FORM}
            onSubmit={createAd} loading={creating} showFileField={true} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
